import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Badge } from "../Components/ui/badge.js";
import { Carousel, CarouselContent, CarouselItem } from "../Components/ui/carousel.js";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../Components/ui/hover-card.js";
import { cn } from "../lib/utils.js";
import { ArrowRightIcon, ArrowLeftIcon } from "lucide-react";
import { useState, useEffect, useCallback, createContext, useContext } from "react";
const InlineCitation = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "span",
  {
    className: cn("tw:group tw:inline tw:items-center tw:gap-1", className),
    ...props
  }
);
const InlineCitationText = ({
  className,
  url,
  children,
  ...props
}) => {
  if (url) {
    return /* @__PURE__ */ jsx(
      "a",
      {
        href: url,
        target: "_blank",
        rel: "noopener noreferrer",
        className: cn(
          "tw:transition-colors tw:group-hover:bg-accent tw:cursor-pointer tw:underline",
          className
        ),
        ...props,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("tw:transition-colors tw:group-hover:bg-accent", className),
      ...props,
      children
    }
  );
};
const InlineCitationCard = (props) => /* @__PURE__ */ jsx(HoverCard, { closeDelay: 0, openDelay: 0, ...props });
const InlineCitationCardTrigger = ({
  sources,
  className,
  ...props
}) => /* @__PURE__ */ jsx(HoverCardTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
  Badge,
  {
    className: cn("tw:ml-1 tw:rounded-full", className),
    variant: "secondary",
    ...props,
    children: sources[0] ? /* @__PURE__ */ jsxs(Fragment, { children: [
      new URL(sources[0]).hostname,
      " ",
      sources.length > 1 && `+${sources.length - 1}`
    ] }) : "unknown"
  }
) });
const InlineCitationCardBody = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(HoverCardContent, { className: cn("tw:relative tw:w-80 tw:p-0", className), ...props });
const CarouselApiContext = createContext(void 0);
const useCarouselApi = () => {
  const context = useContext(CarouselApiContext);
  return context;
};
const InlineCitationCarousel = ({
  className,
  children,
  ...props
}) => {
  const [api, setApi] = useState();
  return /* @__PURE__ */ jsx(CarouselApiContext.Provider, { value: api, children: /* @__PURE__ */ jsx(Carousel, { className: cn("tw:w-full", className), setApi, ...props, children }) });
};
const InlineCitationCarouselContent = (props) => /* @__PURE__ */ jsx(CarouselContent, { ...props });
const InlineCitationCarouselItem = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  CarouselItem,
  {
    className: cn("tw:w-full tw:space-y-2 tw:p-4 tw:pl-8", className),
    ...props
  }
);
const InlineCitationCarouselHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "tw:flex tw:items-center tw:justify-between tw:gap-2 tw:rounded-t-md tw:bg-secondary tw:p-2",
      className
    ),
    ...props
  }
);
const InlineCitationCarouselIndex = ({
  children,
  className,
  ...props
}) => {
  const api = useCarouselApi();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "tw:flex tw:flex-1 tw:items-center tw:justify-end tw:px-3 tw:py-1 tw:text-muted-foreground tw:text-xs",
        className
      ),
      ...props,
      children: children ?? `${current}/${count}`
    }
  );
};
const InlineCitationCarouselPrev = ({
  className,
  ...props
}) => {
  const api = useCarouselApi();
  const handleClick = useCallback(() => {
    if (api) {
      api.scrollPrev();
    }
  }, [api]);
  return /* @__PURE__ */ jsx(
    "button",
    {
      "aria-label": "Previous",
      className: cn("tw:shrink-0", className),
      onClick: handleClick,
      type: "button",
      ...props,
      children: /* @__PURE__ */ jsx(ArrowLeftIcon, { className: "tw:size-4 tw:text-muted-foreground" })
    }
  );
};
const InlineCitationCarouselNext = ({
  className,
  ...props
}) => {
  const api = useCarouselApi();
  const handleClick = useCallback(() => {
    if (api) {
      api.scrollNext();
    }
  }, [api]);
  return /* @__PURE__ */ jsx(
    "button",
    {
      "aria-label": "Next",
      className: cn("tw:shrink-0", className),
      onClick: handleClick,
      type: "button",
      ...props,
      children: /* @__PURE__ */ jsx(ArrowRightIcon, { className: "tw:size-4 tw:text-muted-foreground" })
    }
  );
};
const InlineCitationSource = ({
  title,
  url,
  description,
  className,
  children,
  ...props
}) => /* @__PURE__ */ jsxs("div", { className: cn("tw:space-y-1", className), ...props, children: [
  title && /* @__PURE__ */ jsx("h4", { className: "tw:truncate tw:font-medium tw:text-sm tw:leading-tight", children: url ? /* @__PURE__ */ jsx(
    "a",
    {
      href: url,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "tw:cursor-pointer tw:hover:underline",
      children: title
    }
  ) : title }),
  url && /* @__PURE__ */ jsx(
    "a",
    {
      href: url,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "tw:truncate tw:break-all tw:text-muted-foreground tw:text-xs tw:cursor-pointer tw:hover:underline tw:block",
      children: url
    }
  ),
  description && /* @__PURE__ */ jsx("p", { className: "tw:line-clamp-3 tw:text-muted-foreground tw:text-sm tw:leading-relaxed", children: description }),
  children
] });
const InlineCitationQuote = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "blockquote",
  {
    className: cn(
      "tw:border-muted tw:border-l-2 tw:pl-3 tw:text-muted-foreground tw:text-sm tw:italic",
      className
    ),
    ...props,
    children
  }
);
export {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselItem,
  InlineCitationCarouselNext,
  InlineCitationCarouselPrev,
  InlineCitationQuote,
  InlineCitationSource,
  InlineCitationText
};
//# sourceMappingURL=inline-citation.js.map
